import { useCallback, useState } from 'react';
import { isValidCEP } from '../../../../utils/validation';
import { fetchAddressByZipCode } from '../utils/cep-service';
import { validateShippingAddress, isFieldValid } from '../utils/validation';
import type { ShippingAddressFormProps, ShippingAddressFormState } from '../types/shipping-address.types';
import type { ShippingAddressData } from '../../../../types';

export const useShippingAddress = (props: ShippingAddressFormProps) => {
  const { initialData, onSave } = props;

  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState<Partial<ShippingAddressData>>({});
  const [isFetchingCep, setIsFetchingCep] = useState(false);
  const [isCepValidAndFound, setIsCepValidAndFound] = useState(
    Boolean(initialData.zipCode && initialData.city && initialData.state && isValidCEP(initialData.zipCode))
  );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof ShippingAddressData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  }, [errors]);

  const handleZipCodeChange = useCallback(async (zip: string) => {
    const cleanedZip = zip
      .replace(/\D/g, '')
      .replace(/(\d{5})(\d)/, '$1-$2');

    setFormData(prev => ({ ...prev, zipCode: cleanedZip }));
    setIsCepValidAndFound(false);

    if (cleanedZip.length === 9) {
      setIsFetchingCep(true);
      const addressData = await fetchAddressByZipCode(cleanedZip);
      
      if (addressData && addressData.city && addressData.state) {
        setFormData(prev => ({
          ...prev,
          street: addressData.street ?? '',
          neighborhood: addressData.neighborhood ?? '',
          city: addressData.city ?? '',
          state: addressData.state ?? '',
        }));
        setIsCepValidAndFound(true);
        setErrors(prev => ({
          ...prev,
          zipCode: undefined,
          street: undefined,
          neighborhood: undefined,
          city: undefined,
          state: undefined,
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          street: '',
          neighborhood: '',
          city: '',
          state: '',
        }));
        setIsCepValidAndFound(false);
        setErrors(prev => ({
          ...prev,
          zipCode: 'CEP inválido',
        }));
      }
      setIsFetchingCep(false);
    } else if (cleanedZip.length === 0) {
      setFormData(prev => ({
        ...prev,
        street: '',
        neighborhood: '',
        city: '',
        state: '',
      }));
      setIsCepValidAndFound(false);
      setErrors(prev => ({
        ...prev,
        zipCode: undefined,
      }));
    }
  }, []);

  const validate = useCallback((): boolean => {
    const state: ShippingAddressFormState = {
      formData,
      errors,
      isFetchingCep,
      isCepValidAndFound,
    };
    
    const newErrors = validateShippingAddress(state);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, errors, isFetchingCep, isCepValidAndFound]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
    }
  }, [validate, formData, onSave]);

  // Field validation helpers
  const isZipCodeValid = Boolean(formData.zipCode.trim() && isValidCEP(formData.zipCode) && isCepValidAndFound);
  const isStreetValid = isFieldValid(formData.street, isCepValidAndFound);
  const isNumberValid = isFieldValid(formData.number, isCepValidAndFound);
  const isNeighborhoodValid = isFieldValid(formData.neighborhood, isCepValidAndFound);

  return {
    formData,
    errors,
    isFetchingCep,
    isCepValidAndFound,
    isZipCodeValid,
    isStreetValid,
    isNumberValid,
    isNeighborhoodValid,
    handleChange,
    handleZipCodeChange,
    handleSubmit,
  };
}; 